import React, { useRef, useState } from 'react';
import Layout from '../components/Layout';
import { Mic, Square, Play, Sparkles, Upload as UploadIcon, Wand2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { analyseBeatbox, playDrumSchedule, renderBeatToWav } from '../lib/beatlab';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

const HIT_STYLES = {
  kick:  { color: '#EF4444', label: 'KICK',  y: 66 },
  snare: { color: '#FFB000', label: 'SNARE', y: 40 },
  hihat: { color: '#22C55E', label: 'HAT',   y: 14 },
};

export default function BeatLab() {
  const navigate = useNavigate();
  const [state, setState] = useState('idle');   // idle | recording | analysing | ready
  const [analysis, setAnalysis] = useState(null); // {hits, bpm, duration}
  const [posting, setPosting] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [tempoScale, setTempoScale] = useState(1);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        setState('analysing');
        try {
          const result = await analyseBeatbox(blob);
          if (!result.hits.length) {
            toast.error("Couldn't hear any beats. Try again — louder mic, closer to your mouth.");
            setState('idle');
            return;
          }
          setAnalysis(result);
          setState('ready');
          toast.success(`Found ${result.hits.length} hits · ${result.bpm || '?'} BPM`);
        } catch (err) {
          toast.error('Analysis failed. Try re-recording.');
          setState('idle');
        }
      };
      mr.start();
      recRef.current = mr;
      setState('recording');
    } catch {
      toast.error('Microphone permission denied.');
    }
  };

  const stopRecord = () => {
    const mr = recRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  };

  const playBack = () => {
    if (!analysis) return;
    playDrumSchedule(analysis.hits, tempoScale);
  };

  const applyText = () => {
    // Parse a simple beatbox notation: b = kick, k = snare/clap, t = hi-hat, . = rest
    // Each symbol is a 1/16th note. Example: "b.b.k.t.b.b.k.t."
    const text = prompt.trim().toLowerCase();
    if (!text) return;
    const stepSec = 60 / (analysis?.bpm || 100) / 4;
    const hits = [];
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      let cls = null;
      if (c === 'b' || c === 'k') cls = 'kick';
      else if (c === 's' || c === 'c') cls = 'snare';
      else if (c === 't' || c === 'h') cls = 'hihat';
      if (cls) hits.push({ at_sec: Math.round(i * stepSec * 1000) / 1000, class: cls });
    }
    if (!hits.length) { toast.error('Try "b.s.b.s." or "bttsbtts" — b=kick, s=snare, t=hat'); return; }
    setAnalysis({ hits, bpm: analysis?.bpm || 100, duration: hits[hits.length - 1].at_sec + 0.5 });
    setState('ready');
    toast.success(`Loaded ${hits.length}-hit pattern`);
  };

  const postToFeed = async () => {
    if (!analysis) return;
    setPosting(true);
    try {
      const wav = await renderBeatToWav(analysis.hits, tempoScale);
      const file = new File([wav], `beatlab-${Date.now()}.wav`, { type: 'audio/wav' });
      const form = new FormData();
      form.append('file', file);
      const up = await api.post('/upload/audio', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const title = window.prompt('Name this beat:', `Beat Lab · ${new Date().toLocaleString()}`) || 'Beat Lab Beat';
      await api.post('/posts', {
        title,
        description: `Generated in Beat Lab · ${analysis.hits.length} hits · ${(analysis.bpm || 100)} BPM`,
        genre: 'Beats',
        tags: ['beatlab', 'ai-drums'],
        audio_path: up.data.path,
        duration: Math.ceil(analysis.duration),
        media_type: 'audio',
      });
      toast.success('Beat posted to feed');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post the beat');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Beat Lab</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          Beatbox → real drums.
        </h1>
        <p className="text-[#A1A1AA] text-sm mt-2 max-w-lg">
          Beatbox into your mic — RAW turns every "boom" into a kick, every "tss" into a hi-hat.
          Or type a pattern like <code className="text-[#FFB000]">b.s.b.s</code> and hit generate.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Recorder */}
        <div className="post-card p-6 lg:col-span-1 flex flex-col items-center justify-center" data-testid="beatlab-recorder">
          <motion.button
            onClick={state === 'recording' ? stopRecord : startRecord}
            disabled={state === 'analysing'}
            whileTap={{ scale: 0.94 }}
            className={`relative w-40 h-40 rounded-full border-4 flex flex-col items-center justify-center transition-colors ${
              state === 'recording' ? 'border-[#EF4444] bg-[#EF4444]/10 shadow-[0_0_40px_rgba(239,68,68,0.4)]'
              : state === 'analysing' ? 'border-[#FFB000] bg-[#FFB000]/10'
              : 'border-[#FFB000] bg-[#FFB000]/5 hover:bg-[#FFB000]/15'
            }`}
            data-testid="beatlab-record-btn"
          >
            {state === 'recording'
              ? (<><Square size={32} className="text-[#EF4444]" fill="#EF4444" /><span className="text-[#EF4444] text-xs font-bold mt-2 uppercase tracking-widest">Stop</span></>)
              : state === 'analysing'
              ? (<RefreshCw size={32} className="text-[#FFB000] animate-spin" />)
              : (<><Mic size={32} className="text-[#FFB000]" /><span className="text-[#FFB000] text-xs font-bold mt-2 uppercase tracking-widest">Beatbox</span></>)}
          </motion.button>
          <div className="mt-6 text-center text-[#A1A1AA] text-xs max-w-xs leading-relaxed">
            Tap and beatbox: <span className="text-white font-bold">boom-tss-boom-tss</span> for a house beat,
            or <span className="text-white font-bold">boom-boom-tss</span> for trap.
          </div>
        </div>

        {/* Middle+Right: Analysis */}
        <div className="post-card p-6 lg:col-span-2" data-testid="beatlab-analysis">
          <div className="flex items-center justify-between mb-4">
            <div className="text-white font-heading font-black text-xl">Your beat</div>
            {analysis && (
              <div className="text-xs font-mono uppercase tracking-widest text-[#FFB000]">
                {analysis.hits.length} hits · {analysis.bpm || '?'} BPM
              </div>
            )}
          </div>

          {!analysis && (
            <div className="py-12 text-center border border-dashed border-white/10 rounded-xl">
              <Sparkles size={32} className="mx-auto mb-3 text-[#FFB000]/40" />
              <div className="text-[#A1A1AA] text-sm">Record a beat or write a pattern below.</div>
            </div>
          )}

          {analysis && (
            <>
              {/* Visual grid */}
              <div className="relative h-24 bg-black/40 rounded-xl overflow-hidden mb-4" data-testid="beatlab-grid">
                {[14, 40, 66].map(y => (
                  <div key={y} className="absolute left-0 right-0 h-px bg-white/5" style={{ top: `${y}%` }} />
                ))}
                {analysis.hits.map((h, i) => {
                  const s = HIT_STYLES[h.class];
                  const x = (h.at_sec / (analysis.duration || 1)) * 100;
                  return (
                    <div
                      key={i}
                      className="absolute w-3 h-3 rounded-full -translate-x-1/2 -translate-y-1/2 shadow-[0_0_8px_currentColor]"
                      style={{ left: `${x}%`, top: `${s.y}%`, background: s.color, color: s.color }}
                      title={`${s.label} @ ${h.at_sec}s`}
                    />
                  );
                })}
                <div className="absolute left-2 top-2 text-[9px] uppercase tracking-widest text-[#22C55E] font-bold">Hats</div>
                <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-widest text-[#FFB000] font-bold">Snares</div>
                <div className="absolute left-2 bottom-2 text-[9px] uppercase tracking-widest text-[#EF4444] font-bold">Kicks</div>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <button onClick={playBack} className="btn-pill btn-primary text-xs flex items-center gap-2" data-testid="beatlab-play-btn">
                  <Play size={14} /> Play
                </button>
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">Speed</span>
                  <input
                    type="range" min="0.5" max="2" step="0.05" value={tempoScale}
                    onChange={e => setTempoScale(parseFloat(e.target.value))}
                    className="flex-1 accent-[#FFB000]"
                    data-testid="beatlab-speed"
                  />
                  <span className="text-[10px] font-mono text-[#FFB000] font-bold min-w-[35px]">{tempoScale.toFixed(2)}×</span>
                </div>
              </div>

              <button
                onClick={postToFeed}
                disabled={posting}
                className="btn-pill btn-outline w-full text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="beatlab-post-btn"
              >
                <UploadIcon size={14} /> {posting ? 'Rendering & posting…' : 'Post to my feed'}
              </button>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Or type a pattern</div>
            <div className="flex gap-2">
              <input
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="b.s.b.s.  (b=kick, s=snare, t=hat)"
                className="input-default flex-1 !text-sm"
                data-testid="beatlab-prompt"
              />
              <button
                onClick={applyText}
                className="btn-pill btn-primary text-xs flex items-center gap-2"
                data-testid="beatlab-generate-btn"
              >
                <Wand2 size={14} /> Generate
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
